if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('#sr-country-search-results'),
          // We'll target the <ul> generated by your snippet:
          countriesList: this.querySelector('ul.list-unstyled.countries'),
        };

        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button?.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        // Initial click wiring for any pre-rendered <a>:
        this.refreshItemClickHandlers();
      }

      connectedCallback() {
        this.initDynamicCountries();
      }

      /* ---------- NEW: dynamic countries loader ---------- */
      async initDynamicCountries() {
        if (!this.hasAttribute('data-load-countries')) return;

        try {
          const countries = await this.fetchCountries();
          if (Array.isArray(countries) && countries.length) {
            this.renderCountries(countries);
            // Rewire click handlers after injecting content
            this.refreshItemClickHandlers();
          }
        } catch (err) {
          // Fail silently to keep UX stable, but you can log if needed
          console.error('[localization-form] Failed to load countries', err);
        }
      }

      async fetchCountries() {
        const token = this.getAttribute('data-storefront-token');
        if (!token) throw new Error('Missing data-storefront-token');

        // Adjust API version if you like
        const API_VERSION = '2023-10';
        const endpoint = `/api/${API_VERSION}/graphql.json`;

        const query = `
          query AllLocalizations {
            localization {
              availableCountries {
                isoCode
                name
                availableLanguages {
                  isoCode
                  endonymName
                }
              }
            }
          }
        `;

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query }),
        });

        if (!res.ok) throw new Error(`Storefront API error: ${res.status}`);
        const json = await res.json();
        const countries = json?.data?.localization?.availableCountries || [];
        return countries;
      }

      renderCountries(countries) {
        if (!this.elements.countriesList) return;

        const currentCode =
          (this.elements.input?.value || '').trim().toUpperCase();

        const frag = document.createDocumentFragment();

        countries.forEach((c) => {
          const li = document.createElement('li');
          li.className = 'disclosure__item';
          li.tabIndex = -1;

          const a = document.createElement('a');
          a.className = 'link link--text disclosure__link caption-large focus-inset';
          a.href = '#';
          a.dataset.value = (c.isoCode || '').toUpperCase();
          a.id = c.name || a.dataset.value;

          // aria-current + check visibility like your Liquid
          const isCurrent = a.dataset.value === currentCode;
          if (isCurrent) a.setAttribute('aria-current', 'true');

          // checkmark span (Shopify liquid inlines the svg; we’ll keep it simple)
          const checkSpan = document.createElement('span');
          if (!isCurrent) checkSpan.className = 'visibility-hidden';
          // You can replace this with inline SVG if desired
          checkSpan.innerHTML = '✓';

          const nameSpan = document.createElement('span');
          nameSpan.className = 'country';
          nameSpan.textContent = c.name;

          // Currency span exists in your markup; we don’t get currency from this query.
          // Keep it hidden so the structure matches.
          const currencySpan = document.createElement('span');
          currencySpan.className = 'localization-form__currency motion-reduce hidden';

          a.appendChild(checkSpan);
          a.appendChild(nameSpan);
          a.appendChild(currencySpan);

          li.appendChild(a);
          frag.appendChild(li);
        });

        // Clear any old items (e.g., Liquid rendered none due to `noCountries: true`)
        this.elements.countriesList.innerHTML = '';
        this.elements.countriesList.appendChild(frag);
      }

      refreshItemClickHandlers() {
        this.querySelectorAll('a').forEach((item) => {
          // Avoid double-binding
          item.removeEventListener('click', this.onItemClickBound || (() => {}));
        });
        this.onItemClickBound = this.onItemClick.bind(this);
        this.querySelectorAll('a').forEach((item) => {
          item.addEventListener('click', this.onItemClickBound);
        });
      }
      /* ---------- end new ---------- */

      hidePanel() {
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        // document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer')?.classList.remove('country-selector-open');
        this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();
        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      openSelector() {
        this.elements.button.focus();
        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute(
          'aria-expanded',
          (this.elements.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          // document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }
        document.querySelector('.menu-drawer')?.classList?.add('country-selector-open');
      }

      closeSelector(event) {
        if (
          event.target.classList.contains('country-selector__overlay') ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value || '');
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = 0;

        this.elements.resetButton?.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', Boolean(searchValue));
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country')?.textContent || '');
          const match = countryName.indexOf(searchValue) > -1;
          item.parentElement.classList.toggle('hidden', !match);
          if (match) visibleCountries++;
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector')?.scrollTo?.(0, 0);
        this.querySelector('.country-selector__list')?.scrollTo?.(0, 0);
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon?.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon?.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
